import { useCallback, useEffect, useRef, useState } from 'react';

import { useTracker } from '@archipro-website/tracker';
import { useUserLoggedIn } from '@modules/user';
import { Spinner } from '@rocco/components/spinner';
import type { LoginFlow } from '~/modules/user/hook/use-login-flow';
import type { AuthSource } from '~/modules/user/type';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import { markPasskeyOfferSkipped } from '~/modules/user/passkey/passkey-device-hint';
import type { PasskeyEnrolmentMint } from '~/modules/user/passkey/passkey-enrol-mint';
import { usePasskeyEnrolOffer } from '~/modules/user/passkey/use-passkey-enrol-offer';
import { AuthPanel } from '../component/auth-modal/AuthPanel';
import { SecureSignInOffer } from '../component/auth-modal/SecureSignInOffer';
import {
    trackAuthGateAbandoned,
    trackAuthGateDeciding,
    trackAuthGateOpened,
    trackAuthGateResolved,
} from './auth-gate-events';

/**
 * Where the reader is inside the auth segment. `null` means they are not in it.
 * `deciding` is the moment between a session appearing and knowing whether this
 * device earns the Secure Sign-In ask: the segment holds the surface so the
 * gate's success step cannot flash up and then be replaced by the ask.
 */
export type AuthGateStep = 'auth' | 'deciding' | 'secure-sign-in' | null;

/**
 * How long the segment will hold the surface waiting on the offer decision.
 *
 * Long enough that a normal mint (one round trip) always wins, short enough
 * that a stalled one costs the reader a pause rather than their download.
 */
const DECIDE_TIMEOUT_MS = 2500;

export interface UseAuthGateSegmentOptions {
    /** Selects the intent copy and tags every funnel event from this gate. */
    authSource: AuthSource;
    /**
     * Fired the moment a session exists, before the Secure Sign-In ask. Use for
     * work that should start immediately and run behind the ask — an enquiry
     * resubmit, say — rather than waiting for the reader to resolve it.
     */
    onAuthenticated?: (result?: { isSignUp: boolean }) => void;
    /**
     * Fired when the whole segment is behind the reader: authenticated, and the
     * Secure Sign-In ask either taken or declined. This is where the gated
     * action's own success step belongs.
     */
    onResolved: () => void;
    /**
     * Gives the auth step's first screen a back arrow, for a gate with steps
     * before it — the Get Matched wizard's questions. Most gates have nothing
     * to go back to, and omit it.
     */
    onAuthBack?: () => void;
}

export interface AuthGateSegment {
    step: AuthGateStep;
    /** True while the segment owns the surface, so the shell renders `element`. */
    isActive: boolean;
    /** A guest hit the wall. `publicUser` skips step 0 when detection has run. */
    open: (options?: {
        publicUser?: PublicUser;
        flow?: LoginFlow;
        message?: string;
    }) => void;
    /**
     * The reader arrives already authenticated — a social round-trip, or an
     * account created for them. There is nothing to sign in to, but the Secure
     * Sign-In ask still belongs here, so this resolves straight through when
     * the device does not qualify.
     *
     * `isNewAccount` says the account was made in this journey and so cannot
     * hold a passkey yet, whatever this browser already stores. It defaults to
     * false: a social round-trip is far more often a returning member, and
     * claiming otherwise re-asks people who already enrolled on this device.
     */
    enterAuthenticated: (options?: { isNewAccount?: boolean }) => void;
    /** Records the skip when the reader leaves from the ask, then forgets the segment. */
    close: () => void;
    /** Back to the start, for a fresh open. */
    reset: () => void;
    /** The current step's UI, or `null` when the segment is not active. */
    element: React.ReactNode;
}

/**
 * The `[auth]` segment of every gate, in one place.
 *
 * Each gate is `steps-before → auth → steps-after`, and only the ends differ:
 * an enquiry form, a bulk selection, a download attempt. The middle — sign in
 * or register, then the Secure Sign-In offer, with the funnel events and skip
 * cadence that go with them — is identical everywhere and belongs here, so a
 * change to how people authenticate is made once rather than per gate.
 */
export const useAuthGateSegment = ({
    authSource,
    onAuthenticated,
    onResolved,
    onAuthBack,
}: UseAuthGateSegmentOptions): AuthGateSegment => {
    const { user, isLoggedIn } = useUserLoggedIn();
    const tracker = useTracker();
    const { decideOffer: decidePasskeyOffer, reset: resetPasskeyOffer } =
        usePasskeyEnrolOffer();

    const [step, setStep] = useState<AuthGateStep>(null);
    const [mint, setMint] = useState<PasskeyEnrolmentMint>();
    const [publicUser, setPublicUser] = useState<PublicUser>();
    const [flow, setFlow] = useState<LoginFlow>();
    const [message, setMessage] = useState<string>();

    const stepRef = useRef<AuthGateStep>(null);
    stepRef.current = step;

    /**
     * Identifies the in-flight offer decision. Closing or resetting bumps it, so
     * a decision that resolves after the reader has left cannot reopen the
     * segment behind them.
     */
    const decisionRef = useRef(0);

    /**
     * The ask registered a credential before the reader left it.
     *
     * `SecureSignInOffer` holds a success frame between storing the credential
     * and resolving, so someone who walks away during that frame has enrolled,
     * not declined. Recording a skip there puts both events on one journey and
     * advances a cadence that should have been reset.
     */
    const askEnrolledRef = useRef(false);

    // The in-pane auth step is a login prompt, so it owes the funnel the same
    // impressions the modal-based paths fire.
    useEffect(() => {
        if (step !== 'auth') return;
        tracker.log('LoginPromptShown', {
            url: new URL(window.location.href),
            data: {
                authSource,
                ...(flow && { flow }),
                Source: authSource,
                // `surface: 'gate'` marks this as the in-pane prompt. The
                // legacy modal paths fire LoginPromptShown with the same
                // authSource, and both mechanisms are live through the
                // rollout — without the discriminator the gate funnel credits
                // its auth stage to a prompt the gate never showed.
                ExtraData: JSON.stringify({
                    authSource,
                    ...(flow && { flow }),
                    surface: 'gate',
                }),
            },
        });
        tracker.log('AuthModalImpression', {
            url: new URL(window.location.href),
            data: { Source: authSource },
            ga4Data: { source: authSource },
            targetTracker: ['archiproTracker', 'ga4Tracker'],
        });
    }, [step, authSource, flow, tracker]);

    const open = useCallback<AuthGateSegment['open']>(
        (options) => {
            // Invalidate any decision still in flight. Without this, a mint
            // that settles after the reader has been handed the auth panel can
            // still advance the segment — dismissing the form they are typing
            // into and resolving as though they had authenticated.
            decisionRef.current += 1;
            setPublicUser(options?.publicUser);
            setFlow(options?.flow);
            setMessage(options?.message);
            // One opening, one event — the same rule `enterAuthenticated` keeps
            // below. Gates that re-enter the wall on every blocked submit (the
            // enquiry gate does, once per attempt) would otherwise count a
            // reader who backs out and tries again as two openings, inflating
            // every raw-volume chart over `AuthGateOpened`. The copy and
            // identity above still refresh, because a second attempt can carry
            // a different message or a newly-found public user.
            if (stepRef.current === null) {
                trackAuthGateOpened({ tracker, authSource, entry: 'wall' });
            }
            setStep('auth');
        },
        [tracker, authSource],
    );

    /**
     * Hold the surface while the offer is decided, then either show the ask or
     * hand back to the gate. Detection is asynchronous, so the hold is what
     * keeps the gate's success step from appearing before the ask.
     */
    const decideThenAdvance = useCallback(
        (isNewAccount: boolean) => {
            const decision = ++decisionRef.current;
            setStep('deciding');
            trackAuthGateDeciding({ tracker, authSource });
            // Whichever of the decision and its deadline lands first is the
            // answer; the loser must not also advance a segment that has
            // already moved on.
            let settled = false;
            const advance = (offer: PasskeyEnrolmentMint | null) => {
                if (settled) return;
                settled = true;
                // The reader closed or reset the gate while we were deciding.
                if (decision !== decisionRef.current) return;
                if (offer) {
                    setMint(offer);
                    setStep('secure-sign-in');
                    return;
                }
                setStep(null);
                trackAuthGateResolved({ tracker, authSource, offered: false });
                onResolved();
            };
            // A failed decision hands the gate back rather than holding the
            // surface on `deciding` forever — the ask is optional, the reader's
            // action is not. The same argument covers a decision that never
            // settles at all: minting is a round trip with no timeout of its
            // own, and a stalled one would strand the reader on a bare spinner
            // with their download or enquiry never delivered.
            const deadline = setTimeout(() => advance(null), DECIDE_TIMEOUT_MS);
            const settle = (offer: PasskeyEnrolmentMint | null) => {
                clearTimeout(deadline);
                advance(offer);
            };
            // `isLoggedIn`, not `'ID' in user` — the logged-out user carries
            // `ID: 0`, which reads as a member who never enrolled here.
            void decidePasskeyOffer({
                memberID: isLoggedIn ? user.ID : undefined,
                isNewAccount,
            })
                .then(settle)
                .catch(() => settle(null));
        },
        [decidePasskeyOffer, isLoggedIn, onResolved, user, tracker, authSource],
    );

    const enterAuthenticated = useCallback<
        AuthGateSegment['enterAuthenticated']
    >(
        (options) => {
            // Only meaningful when the reader arrives already authenticated.
            // Once the segment owns the surface it is already handling them,
            // and entering again would restart the decision — dismissing an ask
            // on screen and resolving the gate a second time.
            if (stepRef.current !== null) return;
            trackAuthGateOpened({
                tracker,
                authSource,
                entry: 'authenticated',
            });
            // An account created for the reader mid-enquiry has no passkey of
            // its own, whatever this browser already holds — but a social
            // round-trip usually belongs to a member who does, so only the
            // caller that knows the account is new says so.
            decideThenAdvance(options?.isNewAccount ?? false);
        },
        [decideThenAdvance, tracker, authSource],
    );

    const handleAuthSuccess = useCallback(
        (result?: { isSignUp: boolean }) => {
            onAuthenticated?.(result);
            decideThenAdvance(!!result?.isSignUp);
        },
        [onAuthenticated, decideThenAdvance],
    );

    const handleOfferDone = useCallback(() => {
        setStep(null);
        trackAuthGateResolved({ tracker, authSource, offered: true });
        onResolved();
    }, [onResolved, tracker, authSource]);

    const reset = useCallback(() => {
        // Abandons any decision still in flight, so it cannot land afterwards.
        decisionRef.current += 1;
        askEnrolledRef.current = false;
        setStep(null);
        setMint(undefined);
        setPublicUser(undefined);
        setFlow(undefined);
        setMessage(undefined);
        resetPasskeyOffer();
    }, [resetPasskeyOffer]);

    const close = useCallback(() => {
        const leavingFrom = stepRef.current;
        if (leavingFrom === 'secure-sign-in' && !askEnrolledRef.current) {
            // Leaving from the ask is a skip: record the cadence hint and the
            // funnel event so an abandoned offer is not invisible. Unless the
            // ask was already answered on the way past — see `askEnrolledRef`.
            markPasskeyOfferSkipped();
            tracker.log('PasskeyEnrolSkipped', {
                url: new URL(window.location.href),
                data: { Source: authSource, ExtraData: 'modal_closed' },
            });
        }
        // Closing while the segment holds the surface is the drop-off the gate
        // funnel is for. Closing when it holds nothing is just a dismissal of
        // the host, which the host's own events cover.
        if (leavingFrom !== null) {
            trackAuthGateAbandoned({
                tracker,
                authSource,
                step: leavingFrom,
            });
        }
        reset();
    }, [authSource, tracker, reset]);

    const element =
        step === 'deciding' ? (
            // Deciding now means minting the challenge the ask would spend, so
            // it is a round trip the reader waits through. The segment owns the
            // surface across it, and an owned surface with nothing in it is
            // just an empty sheet.
            <div className="flex min-h-75 flex-1 items-center justify-center">
                <Spinner className="size-9 text-[#828282]" />
            </div>
        ) : step === 'secure-sign-in' && mint ? (
            <SecureSignInOffer
                mint={mint}
                authSource={authSource}
                firstName={
                    'FirstName' in user
                        ? user.FirstName || undefined
                        : undefined
                }
                // `isLoggedIn`, not `'ID' in user` — see decideThenAdvance:
                // the logged-out user carries `ID: 0`, which reads as a member
                // who never enrolled here.
                memberID={isLoggedIn ? user.ID : undefined}
                onEnrolled={() => {
                    askEnrolledRef.current = true;
                }}
                onDone={handleOfferDone}
            />
        ) : step === 'auth' ? (
            <AuthPanel
                authSource={authSource}
                flow={flow}
                defaultPublicUser={publicUser}
                message={message}
                skipPostSignUp
                skipAuthRedirect
                onAuthSuccess={handleAuthSuccess}
                onBack={onAuthBack}
            />
        ) : null;

    return {
        step,
        isActive: step !== null,
        open,
        enterAuthenticated,
        close,
        reset,
        element,
    };
};
